import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    SignOut,
    Plus,
    Trash,
    ArrowLeft,
    Image as ImgIcon,
    Eye,
    EyeSlash,
    FloppyDisk,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "../lib/AuthContext";
import SEO from "../components/SEO";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_brand-builders-43/artifacts/6hieg182_Artboard%2010.svg";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function resolveCover(url) {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${BACKEND}${url.startsWith("/") ? "" : "/"}${url}`;
}

const EMPTY = {
    id: null,
    slug: "",
    title: "",
    excerpt: "",
    category: "Article",
    tags: [],
    content_md: "",
    author: "Studio Cent Douze",
    read_minutes: 5,
    published: false,
    cover_url: null,
};

export default function AdminBlog() {
    const { user, loading, logout, api, setUser } = useAuth();
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [selected, setSelected] = useState(null);

    const refresh = async () => {
        setLoadingList(true);
        try {
            const { data } = await api.get("/admin/blog");
            setArticles(data);
        } catch (err) {
            if (err?.response?.status === 401) {
                setUser(null);
                navigate("/admin");
                return;
            }
            toast.error("Impossible de charger les articles.");
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        if (loading || !user) return;
        refresh();
    }, [loading, user]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return (
            <div className="min-h-screen bg-scd text-cream flex items-center justify-center">
                <span className="label-eyebrow text-cream/60">Chargement…</span>
            </div>
        );
    }
    if (!user) return <Navigate to="/admin" replace />;

    const onLogout = async () => {
        await logout();
        navigate("/admin");
    };

    const onSave = async (article) => {
        try {
            let saved;
            const payload = {
                slug: article.slug || undefined,
                title: article.title,
                excerpt: article.excerpt,
                category: article.category,
                tags: article.tags,
                content_md: article.content_md,
                author: article.author,
                read_minutes: Number(article.read_minutes) || 5,
                published: !!article.published,
            };
            if (article.id) {
                const { data } = await api.put(
                    `/admin/blog/${article.id}`,
                    payload,
                );
                saved = data;
            } else {
                const { data } = await api.post("/admin/blog", payload);
                saved = data;
            }
            toast.success(
                saved.published
                    ? "Article publié."
                    : "Article enregistré comme brouillon.",
            );
            setSelected(saved);
            refresh();
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Impossible d'enregistrer l'article.",
            );
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Supprimer définitivement cet article ?")) return;
        try {
            await api.delete(`/admin/blog/${id}`);
            toast.success("Article supprimé.");
            setSelected(null);
            refresh();
        } catch {
            toast.error("Impossible de supprimer l'article.");
        }
    };

    const onCoverUpload = async (articleId, file) => {
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post(`/admin/blog/${articleId}/cover`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Image de couverture téléversée.");
            setSelected((s) => (s ? { ...s, cover_url: data.cover_url } : s));
            refresh();
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Échec du téléversement.",
            );
        }
    };

    return (
        <main
            data-testid="admin-blog-page"
            className="min-h-screen bg-scd text-cream"
        >
            <SEO title="Journal (admin)" path="/admin/blog" noindex={true} />

            <header className="border-b border-scd bg-[#faf7f1]/90 backdrop-blur-xl sticky top-0 z-40">
                <div className="px-6 md:px-10 py-4 flex items-center justify-between gap-4">
                    <a href="/" className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Studio Cent Douze" className="h-8 w-auto" />
                        <span className="hidden sm:inline label-eyebrow text-yellow">
                            Admin · Journal
                        </span>
                    </a>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/admin/briefs"
                            className="hidden sm:inline label-eyebrow text-cream/70 hover:text-yellow"
                        >
                            Briefs →
                        </Link>
                        <Link
                            to="/admin/analytics"
                            className="hidden sm:inline label-eyebrow text-cream/70 hover:text-yellow"
                        >
                            Analytics →
                        </Link>
                        <Link
                            to="/admin/showreel"
                            className="hidden sm:inline label-eyebrow text-cream/70 hover:text-yellow"
                        >
                            Nos réalisations →
                        </Link>
                        <button
                            onClick={onLogout}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-scd text-cream hover:border-yellow hover:text-yellow text-sm transition-colors"
                        >
                            <SignOut size={16} weight="bold" />
                            Déconnexion
                        </button>
                    </div>
                </div>
            </header>

            <section className="px-6 md:px-10 py-10 max-w-7xl mx-auto">
                {selected ? (
                    <Editor
                        article={selected}
                        onBack={() => setSelected(null)}
                        onSave={onSave}
                        onDelete={onDelete}
                        onCoverUpload={onCoverUpload}
                    />
                ) : (
                    <ListView
                        articles={articles}
                        loading={loadingList}
                        onSelect={setSelected}
                        onCreate={() => setSelected({ ...EMPTY })}
                    />
                )}
            </section>
        </main>
    );
}

// ------------------------------------------------------------
//                       List view
// ------------------------------------------------------------
function ListView({ articles, loading, onSelect, onCreate }) {
    return (
        <>
            <div className="flex items-end justify-between gap-4 mb-8">
                <div>
                    <span className="label-eyebrow text-yellow">— Journal</span>
                    <h1 className="font-display text-3xl md:text-4xl mt-1">
                        Articles
                    </h1>
                    <p className="text-cream/60 text-sm mt-2">
                        {articles.length} article{articles.length > 1 ? "s" : ""} au
                        total ·{" "}
                        {articles.filter((a) => a.published).length} publié
                        {articles.filter((a) => a.published).length > 1 ? "s" : ""}
                    </p>
                </div>
                <button
                    data-testid="admin-blog-new"
                    onClick={onCreate}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-yellow text-[#272727] font-semibold text-sm hover:bg-green hover:text-[#faf7f1] transition-colors"
                >
                    <Plus size={16} weight="bold" />
                    Nouvel article
                </button>
            </div>

            {loading ? (
                <p className="text-center py-16 label-eyebrow text-cream/50">
                    Chargement…
                </p>
            ) : articles.length === 0 ? (
                <p className="text-center py-16 label-eyebrow text-cream/50">
                    Aucun article. Clique sur « Nouvel article » pour commencer.
                </p>
            ) : (
                <div className="grid gap-3">
                    {articles.map((a) => (
                        <button
                            key={a.id}
                            type="button"
                            onClick={() => onSelect(a)}
                            data-testid={`admin-blog-row-${a.slug}`}
                            className="text-left border border-scd bg-elev rounded-sm p-5 hover:border-yellow transition-colors"
                        >
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-sm bg-scd overflow-hidden shrink-0">
                                    {a.cover_url && (
                                        <img
                                            src={resolveCover(a.cover_url)}
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-medium ${
                                                a.published
                                                    ? "bg-yellow text-[#272727]"
                                                    : "bg-cream/10 text-cream/60"
                                            }`}
                                        >
                                            {a.published ? (
                                                <Eye size={10} weight="fill" />
                                            ) : (
                                                <EyeSlash size={10} weight="fill" />
                                            )}
                                            {a.published ? "Publié" : "Brouillon"}
                                        </span>
                                        <span className="text-[10px] uppercase tracking-wider text-cream/50">
                                            {a.category}
                                        </span>
                                    </div>
                                    <h3 className="mt-1.5 font-display text-lg text-cream truncate">
                                        {a.title}
                                    </h3>
                                    <p className="text-xs text-cream/55 font-mono truncate">
                                        /blog/{a.slug}
                                    </p>
                                </div>
                                <span className="text-xs text-cream/50 whitespace-nowrap">
                                    {a.read_minutes} min
                                </span>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </>
    );
}

// ------------------------------------------------------------
//                       Editor
// ------------------------------------------------------------
function Editor({ article, onBack, onSave, onDelete, onCoverUpload }) {
    const [draft, setDraft] = useState(article);

    useEffect(() => setDraft(article), [article]);

    const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

    return (
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
            <div className="min-w-0">
                <button
                    onClick={onBack}
                    data-testid="admin-blog-back"
                    className="inline-flex items-center gap-2 text-cream/70 hover:text-yellow text-sm mb-6"
                >
                    <ArrowLeft size={14} weight="bold" />
                    Retour à la liste
                </button>

                <Field label="Titre">
                    <input
                        data-testid="admin-blog-title"
                        value={draft.title}
                        onChange={(e) => set({ title: e.target.value })}
                        className="w-full bg-elev border border-scd rounded-sm px-4 py-3 text-cream text-lg font-display focus:border-yellow outline-none"
                        placeholder="Titre de l'article…"
                    />
                </Field>

                <Field label="Slug (URL)">
                    <div className="flex items-center gap-2">
                        <span className="text-cream/40 text-sm">/blog/</span>
                        <input
                            data-testid="admin-blog-slug"
                            value={draft.slug || ""}
                            onChange={(e) => set({ slug: e.target.value })}
                            placeholder="auto-généré si vide"
                            className="flex-1 bg-elev border border-scd rounded-sm px-3 py-2 text-cream font-mono text-sm focus:border-yellow outline-none"
                        />
                    </div>
                </Field>

                <Field label="Résumé (excerpt)">
                    <textarea
                        data-testid="admin-blog-excerpt"
                        value={draft.excerpt || ""}
                        onChange={(e) => set({ excerpt: e.target.value })}
                        rows={2}
                        className="w-full bg-elev border border-scd rounded-sm px-4 py-3 text-cream focus:border-yellow outline-none resize-none"
                        placeholder="Résumé affiché dans la liste et sur les partages…"
                    />
                </Field>

                <Field
                    label="Contenu (Markdown)"
                    hint="Titres avec ## , listes avec - , gras avec **texte**, liens [texte](url)"
                >
                    <textarea
                        data-testid="admin-blog-content"
                        value={draft.content_md || ""}
                        onChange={(e) => set({ content_md: e.target.value })}
                        rows={20}
                        className="w-full bg-elev border border-scd rounded-sm px-4 py-3 text-cream focus:border-yellow outline-none font-mono text-sm leading-relaxed"
                    />
                </Field>
            </div>

            <aside className="space-y-4">
                <div className="border border-scd bg-elev rounded-sm p-5">
                    <div className="flex items-center justify-between">
                        <span className="label-eyebrow text-cream/60">
                            Publication
                        </span>
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!draft.published}
                                onChange={(e) => set({ published: e.target.checked })}
                                data-testid="admin-blog-published"
                                className="w-4 h-4 accent-yellow-500"
                            />
                            <span className="text-sm text-cream">Publié</span>
                        </label>
                    </div>
                    <button
                        onClick={() => onSave(draft)}
                        data-testid="admin-blog-save"
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-yellow text-[#272727] font-semibold text-sm hover:bg-green hover:text-[#faf7f1] transition-colors"
                    >
                        <FloppyDisk size={16} weight="bold" />
                        Enregistrer
                    </button>
                    {draft.id && (
                        <button
                            onClick={() => onDelete(draft.id)}
                            className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-scd text-cream/70 text-xs hover:border-[#ff6b6b] hover:text-[#ff6b6b] transition-colors"
                        >
                            <Trash size={12} weight="bold" />
                            Supprimer
                        </button>
                    )}
                </div>

                <div className="border border-scd bg-elev rounded-sm p-5">
                    <span className="label-eyebrow text-cream/60">
                        Image de couverture
                    </span>
                    <div className="mt-3">
                        {draft.cover_url ? (
                            <img
                                src={resolveCover(draft.cover_url)}
                                alt=""
                                className="w-full aspect-video object-cover rounded-sm border border-scd"
                            />
                        ) : (
                            <div className="w-full aspect-video border border-dashed border-scd rounded-sm grid place-items-center text-cream/40">
                                <ImgIcon size={28} weight="duotone" />
                            </div>
                        )}
                        <input
                            type="file"
                            id="cover-upload"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(e) => {
                                if (!draft.id) {
                                    toast.info(
                                        "Enregistre d'abord l'article, puis téléverse la couverture.",
                                    );
                                    return;
                                }
                                if (e.target.files?.[0]) {
                                    onCoverUpload(draft.id, e.target.files[0]);
                                    e.target.value = "";
                                }
                            }}
                            className="hidden"
                        />
                        <label
                            htmlFor="cover-upload"
                            data-testid="admin-blog-cover-upload"
                            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-scd text-cream/85 text-xs hover:border-yellow hover:text-yellow transition-colors cursor-pointer"
                        >
                            {draft.cover_url ? "Remplacer" : "Téléverser"}
                        </label>
                    </div>
                </div>

                <div className="border border-scd bg-elev rounded-sm p-5 space-y-3">
                    <Field label="Catégorie" compact>
                        <input
                            value={draft.category || ""}
                            onChange={(e) => set({ category: e.target.value })}
                            className="w-full bg-scd border border-scd rounded-sm px-3 py-2 text-cream text-sm focus:border-yellow outline-none"
                            placeholder="Guides, Branding, Photo & Vidéo…"
                        />
                    </Field>

                    <Field label="Tags (virgules)" compact>
                        <input
                            value={(draft.tags || []).join(", ")}
                            onChange={(e) =>
                                set({
                                    tags: e.target.value
                                        .split(",")
                                        .map((t) => t.trim())
                                        .filter(Boolean),
                                })
                            }
                            className="w-full bg-scd border border-scd rounded-sm px-3 py-2 text-cream text-sm focus:border-yellow outline-none"
                            placeholder="prix, PME, site web"
                        />
                    </Field>

                    <Field label="Auteur" compact>
                        <input
                            value={draft.author || ""}
                            onChange={(e) => set({ author: e.target.value })}
                            className="w-full bg-scd border border-scd rounded-sm px-3 py-2 text-cream text-sm focus:border-yellow outline-none"
                        />
                    </Field>

                    <Field label="Temps de lecture (min)" compact>
                        <input
                            type="number"
                            min={1}
                            max={60}
                            value={draft.read_minutes || 5}
                            onChange={(e) => set({ read_minutes: e.target.value })}
                            className="w-24 bg-scd border border-scd rounded-sm px-3 py-2 text-cream text-sm focus:border-yellow outline-none"
                        />
                    </Field>
                </div>
            </aside>
        </div>
    );
}

function Field({ label, hint, children, compact = false }) {
    return (
        <label className={compact ? "block" : "block mb-5"}>
            <span className="label-eyebrow text-cream/60 mb-2 block">{label}</span>
            {children}
            {hint && (
                <span className="mt-1.5 block text-[11px] text-cream/45 leading-relaxed">
                    {hint}
                </span>
            )}
        </label>
    );
}
